"use client";

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Bed, Bath, MapPin, Loader2, Star, X, Home, Check, ChevronsUpDown } from 'lucide-react';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { getPrimaryPropertyImageUrl, shouldUnoptimizedPropertyImage } from '@/lib/property-image';
import { useVideoLazyLoad } from '@/hooks/useVideoLazyLoad';

const CMS_API_URL = process.env.NEXT_PUBLIC_CMS_API_URL || 'http://localhost:3001';

// Property interface
interface Property {
    id: string;
    title: string;
    description: string | null;
    price: number;
    property_category?: string | null;
    location: string;
    bedrooms: number;
    bathrooms: number;
    area_sqft: number | null;
    status: string;
    images: string[] | null;
    cover_image: string[] | null;
    featured: boolean;
    amenities: string[] | null;
    type_of_room?: string | null;
}

interface PropertyTypesResponse {
    success: boolean;
    data: { value: string; label: string }[];
}

interface LocationItem {
    id: string;
    name: string;
    short_name: string;
    slug: string;
    area_type: string;
    is_active: boolean;
}

// Price range options with min/max values
const PRICE_RANGES = [
    { value: '0-3000', label: 'Under AED 3,000', min: 0, max: 3000 },
    { value: '3001-5000', label: 'AED 3,001 - 5,000', min: 3001, max: 5000 },
    { value: '5001-10000', label: 'AED 5,001 - 10,000', min: 5001, max: 10000 },
    { value: '10001-20000', label: 'AED 10,001 - 20,000', min: 10001, max: 20000 },
    { value: '20000+', label: 'AED 20,000+', min: 20000, max: undefined },
];

// Helper functions
function getPropertyImage(property: Property): string | null {
    return getPrimaryPropertyImageUrl(property);
}

function formatPrice(price: number): string {
    return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(price);
}

function normalizeTypeValue(value?: string | null): string {
    return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export default function Hero() {
    const router = useRouter();
    // Search form state
    const [propertyType, setPropertyType] = useState('');
    const [price, setPrice] = useState('');
    const [location, setLocation] = useState('');
    const [bedrooms, setBedrooms] = useState('');
    const [openLocation, setOpenLocation] = useState(false);

    // Dynamic locations from API
    const [locations, setLocations] = useState<LocationItem[]>([]);
    const [propertyTypes, setPropertyTypes] = useState<{ value: string; label: string }[]>([]);
    const [roomTypes, setRoomTypes] = useState<{ value: string; label: string; property_type_id: string }[]>([]);
    const [loadingLocations, setLoadingLocations] = useState(true);
    const [loadingPropertyTypes, setLoadingPropertyTypes] = useState(true);

    // Search results state
    const [properties, setProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Ref for scrolling to results
    const resultsRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    useVideoLazyLoad(videoRef);

    // Fetch locations from API
    useEffect(() => {
        async function fetchMetadata() {
            try {
                const [locationsResponse, typesResponse, roomTypesResponse] = await Promise.all([
                    fetch(`${CMS_API_URL}/api/properties/meta/locations`),
                    fetch(`${CMS_API_URL}/api/properties/meta/types`),
                    fetch(`${CMS_API_URL}/api/properties/meta/room-types`),
                ]);

                if (locationsResponse.ok) {
                    const locationsData = await locationsResponse.json();
                    if (locationsData.success && locationsData.data) {
                        const items = locationsData.data;
                        if (items.length > 0 && typeof items[0] === 'object') {
                            setLocations(items);
                        } else if (locationsData.legacy_strings) {
                            setLocations(locationsData.legacy_strings.map((s: string) => ({ id: '', name: s, short_name: s, slug: '', area_type: '', is_active: true })));
                        }
                    }
                }

                if (typesResponse.ok) {
                    const typesData: PropertyTypesResponse = await typesResponse.json();
                    if (typesData.success && Array.isArray(typesData.data)) {
                        setPropertyTypes(typesData.data);
                        // Set default to 'Full Unit' if nothing selected
                        // Default to empty as per user request
                        // if (!propertyType) {
                        //     const fullUnit = typesData.data.find(t => 
                        //         t.label.toLowerCase() === 'full unit'
                        //     );
                        //     if (fullUnit) setPropertyType(fullUnit.value);
                        // }
                    }
                }

                if (roomTypesResponse.ok) {
                    const roomTypesData = await roomTypesResponse.json();
                    if (roomTypesData.success && Array.isArray(roomTypesData.data)) {
                        setRoomTypes(roomTypesData.data);
                    }
                }
            } catch (error) {
                console.error('Error fetching metadata:', error);
            } finally {
                setLoadingLocations(false);
                setLoadingPropertyTypes(false);
            }
        }
        fetchMetadata();
    }, []);

    // Handle tag click to set property type
    const handleTagClick = (tag: string) => {
        const tagToType: Record<string, string> = {
            'House': 'house',
            'Apartment': 'apartment',
            'Villa': 'villa',
        };
        setPropertyType(tagToType[tag] || '');
    };

    // Handle search submit - now redirects to /properties page
    const handleSearch = () => {
        const params = new URLSearchParams();

        if (location) params.append('location', location);

        // Price range
        if (price) {
            const priceRange = PRICE_RANGES.find(r => r.value === price);
            if (priceRange) {
                if (priceRange.min !== undefined) params.append('minPrice', priceRange.min.toString());
                if (priceRange.max !== undefined) params.append('maxPrice', priceRange.max.toString());
            }
        }

        // Room type
        if (bedrooms && bedrooms !== 'all') {
            const selectedRoomType = roomTypes.find(rt => rt.value === bedrooms);
            if (selectedRoomType) {
                params.append('type_of_room', selectedRoomType.label);
            } else {
                params.append('type_of_room', bedrooms);
            }
        }

        // Property type
        if (propertyType && propertyType !== 'all') {
            const selectedType = propertyTypes.find(type => type.value === propertyType);
            if (selectedType) {
                params.append('looking_for', selectedType.label);
            } else {
                params.append('looking_for', propertyType);
            }
        }

        // Redirect to properties page
        router.push(`/properties?${params.toString()}`);
    };

    // Clear search results
    const clearSearch = () => {
        setHasSearched(false);
        setProperties([]);
        setPropertyType('');
        setPrice('');
        setLocation('');
        setBedrooms('');
    };

    const selectedPropertyTypeLabel =
        propertyTypes.find((type) => normalizeTypeValue(type.value) === normalizeTypeValue(propertyType))?.label ||
        propertyType;

    return (
        <>
            <div className="relative w-full min-h-screen flex flex-col overflow-hidden">
                {/* Background Video */}
                <div className="absolute inset-0 z-0 overflow-hidden">
                    <video
                        ref={videoRef}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="absolute w-full h-full object-cover"
                    >
                        <source src="/video/bg_video.mp4" type="video/mp4" />
                    </video>
                    {/* Dark Gradient Overlay */}
                    <div className="absolute inset-0 bg-linear-to-b from-black/50 via-black/20 to-black/30" />
                </div>

                {/* Main Content */}
                <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col flex-1 pt-20 sm:pt-24 md:pt-28 pb-4 sm:pb-6 md:pb-8">

                    {/* Hero Text Section */}
                    <div className="flex-1 flex flex-col relative top-4 sm:top-8  justify-center">
                        {/* Tags - Clickable to set property type */}
                        {/* <div className="flex flex-wrap gap-2 sm:gap-3 mb-3 sm:mb-4">
                            {['House', 'Apartment', 'Villa'].map((tag) => {
                                const tagToType: Record<string, string> = {
                                    'House': 'house',
                                    'Apartment': 'apartment',
                                    'Villa': 'villa',
                                };
                                const isActive = propertyType === tagToType[tag];
                                return (
                                    <button
                                        key={tag}
                                        onClick={() => handleTagClick(tag)}
                                        className={`px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors cursor-pointer shadow-sm ${isActive
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                                            }`}
                                    >
                                        {tag}
                                    </button>
                                );
                            })}
                        </div> */}

                        <div className="flex flex-col  lg:items-start lg:justify-between gap-4 sm:gap-6 lg:gap-8">
                            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight leading-[1.15] sm:leading-[1.1]">
                                Rooms & Apartments for Rent in Dubai
                            </h1>
                            <small className="text-xl sm:text-xl md:text-xl lg:text-xl font-medium text-white tracking-tight leading-[1.15] sm:leading-[1.1]"> AI-Powered Network of <br className="hidden xs:block" /> Fully Furnished Rental Homes in Dubai</small>

                            <p className="max-w-lg invisible text-white/80 text-xs sm:text-sm leading-relaxed lg:text-right lg:pb-2">
                                Flexible living for modern residents and higher, hassle-free returns for property owners – powered by intelligent operations, hotel-grade standards, and a fully digital experience.
                            </p>
                        </div>
                    </div>

                    {/* Search Card Section */}
                    <div className="bg-background rounded-xl sm:rounded-2xl p-3 sm:p-5 shadow-2xl w-full mt-4 ">
                        <h2 className="text-base sm:text-lg md:text-xl font-bold text-foreground mb-3 sm:mb-4">Find the best place</h2>

                        {/* Search Fields Grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-4">

                            {/* Looking for (Property Type) */}
                            <div className="space-y-1">
                                <label className="text-[10px] sm:text-xs font-semibold text-foreground block">Looking for</label>
                                <Select value={propertyType} onValueChange={(val) => {
                                    setPropertyType(val);
                                    setBedrooms('');
                                }}>
                                    <SelectTrigger aria-label="Looking for" className="w-full bg-muted border-border rounded-md sm:rounded-lg h-9 sm:h-10 text-xs sm:text-sm data-[placeholder]:text-gray-700">
                                        <SelectValue placeholder={loadingPropertyTypes ? "Loading..." : "Select Type"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Types</SelectItem>
                                        {propertyTypes.filter(t => !['private', 'test'].includes(t.value.toLowerCase())).map((type) => (
                                            <SelectItem key={type.value} value={type.value}>
                                                {type.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Price */}
                            <div className="space-y-1">
                                <label className="text-[10px] sm:text-xs font-semibold text-foreground block">Price per month</label>
                                <Select value={price} onValueChange={setPrice}>
                                    <SelectTrigger aria-label="price" className="w-full bg-muted border-border rounded-md sm:rounded-lg h-9 sm:h-10 text-xs sm:text-sm data-[placeholder]:text-gray-700">
                                        <SelectValue placeholder="Select Price" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PRICE_RANGES.map((range) => (
                                            <SelectItem key={range.value} value={range.value}>
                                                {range.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Looking From */}
                            <div className="space-y-1">
                                <label className="text-[10px] sm:text-xs font-semibold text-foreground block">Looking from</label>
                                <Popover open={openLocation} onOpenChange={setOpenLocation}>
                                    <PopoverTrigger asChild>
                                        <button
                                            disabled={loadingLocations}
                                            className={cn(
                                                "flex w-full items-center justify-between bg-muted border border-border rounded-md sm:rounded-lg px-3 py-2 text-xs sm:text-sm text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
                                                !location && "text-gray-700"
                                            )}
                                        >
                                            <span className="truncate flex-1">
                                                {loadingLocations ? "Loading..." : location ? location : "Select Location"}
                                            </span>
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[200px] sm:w-[250px] p-0" align="start">
                                        <Command>
                                            <CommandInput placeholder="Search area name..." />
                                            <CommandList>
                                                <CommandEmpty>No location found.</CommandEmpty>
                                                <CommandGroup>
                                                    {locations.length > 0 ? (
                                                        <>
                                                            <CommandItem
                                                                value="All Locations"
                                                                onSelect={() => {
                                                                    setLocation("");
                                                                    setOpenLocation(false);
                                                                }}
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        "mr-2 h-4 w-4",
                                                                        location === "" ? "opacity-100" : "opacity-0"
                                                                    )}
                                                                />
                                                                All Locations
                                                            </CommandItem>
                                                            {locations.map((loc) => (
                                                                <CommandItem
                                                                    key={loc.id || loc.name}
                                                                    value={`${loc.short_name} ${loc.name}`}
                                                                    onSelect={() => {
                                                                        setLocation(loc.name);
                                                                        setOpenLocation(false);
                                                                    }}
                                                                >
                                                                    <Check
                                                                        className={cn(
                                                                            "mr-2 h-4 w-4",
                                                                            location === loc.name ? "opacity-100" : "opacity-0"
                                                                        )}
                                                                    />
                                                                    {loc.short_name}
                                                                </CommandItem>
                                                            ))}
                                                        </>
                                                    ) : (
                                                        <CommandItem disabled>No locations available</CommandItem>
                                                    )}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* Type of room */}
                            <div className="space-y-1">
                                <label className="text-[10px] sm:text-xs font-semibold text-foreground block">Type of room</label>
                                <Select value={bedrooms} onValueChange={setBedrooms}>
                                    <SelectTrigger aria-label="bedrooms" className="w-full bg-muted border-border rounded-md sm:rounded-lg h-9 sm:h-10 text-xs sm:text-sm data-[placeholder]:text-gray-700">
                                        <SelectValue placeholder={loadingPropertyTypes ? "Loading..." : "Select Type"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Rooms</SelectItem>
                                        {roomTypes
                                            .filter(rt => !propertyType || propertyType === 'all' || rt.property_type_id === propertyType)
                                            .map((rt) => (
                                                <SelectItem key={rt.value} value={rt.value}>
                                                    {rt.label}
                                                </SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </div>

                        </div>

                        {/* Footer with Search Button */}
                        <div className="flex justify-end gap-3 pt-2 sm:pt-3 border-t border-border">
                            <button
                                onClick={handleSearch}
                                disabled={loading}
                                className="bg-primary text-primary-foreground px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs font-semibold hover:bg-primary/90 transition-all shadow-lg w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Searching...
                                    </>
                                ) : (
                                    'Search Properties'
                                )}
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            {/* Search results section removed - redirecting to /properties page instead */}
        </>
    );
}
